import { FC, useMemo, useState } from "react";
import { intervalToDuration, isEqual } from "date-fns";

import { IScheduleBlockEntry } from "Bands/types";
import {
  FlexContainer,
  Icon,
  MutedDarkText,
  MutedText,
  SmallText,
  StatusText,
  TinyText,
} from "shared/styles";
import { Clock } from "shared/assets/icons";
import { colors } from "shared/styles/constants";
import {
  formatDateTime,
  formatTime,
  getRegionalSettingsOptions,
  utcStringToDateTimeTimezone,
} from "shared/utils";
import { IStatus } from "shared/types";
import { Spacer } from "shared/layout";
import { Tooltip } from "shared/components";
import { useBandRegionalSettings } from "Bands/utils";

const { timezoneToUtc } = getRegionalSettingsOptions();

interface ScheduleBlockEntryTimeProps
  extends Pick<
    IScheduleBlockEntry,
    "start" | "end" | "startTimezone" | "endTimezone"
  > {}
export const ScheduleBlockEntryTime: FC<ScheduleBlockEntryTimeProps> = ({
  start,
  end,
  startTimezone,
  endTimezone,
}) => {
  const { dateFormat, timeFormat } = useBandRegionalSettings();
  const startDate = useMemo(() => utcStringToDateTimeTimezone(start), [start]);
  const endDate = useMemo(() => utcStringToDateTimeTimezone(end), [end]);
  const endFormattedDate = useMemo(
    () => formatDateTime(end, { dateOnly: true, dateFormat }),
    [dateFormat, end]
  );

  const startTime = useMemo(
    () => formatTime({ time: startDate.time, timeFormat }),
    [startDate.time, timeFormat]
  );

  const endTime = useMemo(
    () => formatTime({ time: endDate.time, timeFormat }),
    [endDate.time, timeFormat]
  );

  const startTZ = useMemo(
    () => timezoneToUtc(startTimezone, null, true),
    [startTimezone]
  );
  const endTZ = useMemo(
    () => timezoneToUtc(endTimezone, null, true),
    [endTimezone]
  );

  const duration = useMemo(
    () =>
      intervalToDuration({
        start: new Date(start),
        end: new Date(end),
      }),
    [end, start]
  );

  const isStartEqualToEnd = useMemo(
    () => isEqual(new Date(start), new Date(end)),
    [end, start]
  );

  const [referenceElement, setReferenceElement] = useState<Element | null>();

  return (
    <div style={{ width: 200, flex: "0 0 auto" }}>
      <FlexContainer
        align="flex-start"
        flexWrap="nowrap"
        ref={setReferenceElement}
        style={{ display: "inline-flex" }}
      >
        <Icon as={Clock} color={colors.grey4} style={{ marginTop: "6px" }} />
        <Spacer size="xxs">
          <SmallText as={MutedText}>{startTime}</SmallText>
          {!isStartEqualToEnd && startTZ !== endTZ && (
            <MutedDarkText as={TinyText}>{startTZ}</MutedDarkText>
          )}
        </Spacer>
        {!isStartEqualToEnd && <SmallText as={MutedText}>-</SmallText>}
        {!isStartEqualToEnd && (
          <Spacer size="xxs">
            <SmallText as={MutedText}>{endTime}</SmallText>
            {startTZ !== endTZ && (
              <MutedDarkText as={TinyText}>{endTZ}</MutedDarkText>
            )}
            {startDate.date !== endDate.date && (
              <StatusText status={IStatus.warning} as={TinyText}>
                {endFormattedDate}
              </StatusText>
            )}
          </Spacer>
        )}
        {!isStartEqualToEnd && (
          <Tooltip
            content={
              <TinyText>
                {!!duration.years && (
                  <>
                    {duration.years} {duration.years === 1 ? "year " : "years "}
                  </>
                )}
                {!!duration.months && (
                  <>
                    {duration.months}{" "}
                    {duration.months === 1 ? "month " : "months "}
                  </>
                )}
                {!!duration.days && (
                  <>
                    {duration.days} {duration.days === 1 ? "day " : "days "}
                  </>
                )}
                {!!duration.hours && `${duration.hours}h `}
                {!!duration.minutes && `${duration.minutes}min`}
              </TinyText>
            }
            referenceElement={referenceElement}
            placement="top"
          />
        )}
      </FlexContainer>
    </div>
  );
};
